// components/voice/VoiceChatHistory.tsx
import { useRef, useEffect } from 'react';
import { ChatMessage, ConnectionState } from '../../contexts/voice/types';

interface VoiceChatHistoryProps {
  messages: ChatMessage[];
  currentUserText: string;
  currentAssistantText: string;
  state: ConnectionState;
  isTyping: boolean;
  chatMode: 'voice' | 'text';
  emptyStateText: string;
  className?: string;
}

export function VoiceChatHistory({
  messages,
  currentUserText,
  currentAssistantText,
  state,
  isTyping,
  chatMode,
  emptyStateText,
  className = '',
}: VoiceChatHistoryProps) {
  const chatEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, currentUserText, currentAssistantText]);

  return (
    <div className={`overflow-y-auto p-4 space-y-3 ${className}`}>
      {messages.length === 0 && state === 'disconnected' && (
        <div className="text-center text-muted-foreground text-sm py-8">
          {emptyStateText}
        </div>
      )}

      {messages.map((msg, i) => (
        <div key={i} className={`flex ${
          msg.role === 'user' ? 'justify-end' :
          msg.role === 'system' ? 'justify-center' : 'justify-start'
        }`}>
          <div className={`max-w-[85%] px-3 py-2 rounded-xl text-sm ${
            msg.role === 'user'
              ? 'bg-primary text-primary-foreground rounded-br-sm'
              : msg.role === 'system'
              ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200 text-xs font-mono border border-yellow-300 dark:border-yellow-700 whitespace-pre-wrap break-words'
              : 'bg-muted rounded-bl-sm'
          }`}>
            {msg.content}
          </div>
        </div>
      ))}

      {currentUserText && (
        <div className="flex justify-end">
          <div className="max-w-[85%] px-3 py-2 rounded-xl bg-primary/70 text-primary-foreground rounded-br-sm text-sm">
            {currentUserText}
            <span className="inline-block w-1 h-3 ml-1 bg-white/60 rounded-sm animate-pulse align-middle" />
          </div>
        </div>
      )}

      {currentAssistantText && (
        <div className="flex justify-start">
          <div className="max-w-[85%] px-3 py-2 rounded-xl bg-muted rounded-bl-sm text-sm">
            {currentAssistantText}
            {state === 'listening' && (
              <span className="inline-block w-1 h-3 ml-1 bg-primary/60 rounded-sm animate-pulse align-middle" />
            )}
          </div>
        </div>
      )}

      {/* Typing indicator */}
      {isTyping && chatMode === 'text' && (
        <div className="flex justify-start">
          <div className="bg-muted/50 rounded-2xl rounded-bl-md px-4 py-2">
            <div className="flex items-center gap-1">
              <span className="w-2 h-2 bg-primary/60 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
              <span className="w-2 h-2 bg-primary/60 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
              <span className="w-2 h-2 bg-primary/60 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
            </div>
          </div>
        </div>
      )}

      <div ref={chatEndRef} />
    </div>
  );
}
